/**
 * ResultsHeader — top bar of the center (results) column on the Base screen.
 * Shows "Найдено N" (mono count from useCount), active facets flattened into
 * removable chips (each value of an among/eq clause becomes one chip; ✕ removes
 * just that value, rebuilding the clause and writing back via setFilters), and a
 * right-side cluster (Columns stub · Sort display + cycle · Export).
 * Styling per mockup_2.dc.html results-header §; replaces ActiveFilterChips here.
 */

import { useMemo, useState } from "react";

import type { FilterClause, Refs } from "@/api/client";
import { useCount, useRefs } from "@/api/hooks";
import { FIELD_BY_KEY, resolveOptions } from "@/lib/fields";
import { useFilters, parseSort, cycleSort } from "@/store/filters";
import { useSelection } from "@/store/selection";
import { ExportButton } from "@/components/ExportButton";
import { useColumns, COLUMNS } from "@/store/columns";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";

/** One flat (clause-index, value) chip derived from the applied filter list. */
interface FacetChip {
  field: string;
  value: string;
  fieldLabel: string;
  valueLabel: string;
}

/** Resolve a raw clause value to a human label via refs (falls back to the value). */
function labelForValue(field: string, value: string, refs: Refs | undefined): string {
  const def = FIELD_BY_KEY[field];
  if (!def) return value;
  const opt = resolveOptions(def, refs).find((o) => String(o.value) === value);
  return opt ? opt.label : value;
}

/** Flatten filters[] → one chip per value (among arrays expand; eq is single). */
function flattenChips(filters: FilterClause[], refs: Refs | undefined): FacetChip[] {
  const chips: FacetChip[] = [];
  for (const c of filters) {
    const fieldLabel = FIELD_BY_KEY[c.field]?.label ?? c.field;
    const values = Array.isArray(c.value)
      ? c.value.map(String)
      : c.value == null
        ? []
        : [String(c.value)];
    for (const v of values) {
      chips.push({
        field: c.field,
        value: v,
        fieldLabel,
        valueLabel: labelForValue(c.field, v, refs),
      });
    }
  }
  return chips;
}

/** Russian label for the current sort column (default: Фамилия ↑). */
const SORT_DEFAULT_FIELD = "last_name";

export function ResultsHeader() {
  const { data: refs } = useRefs();
  const filters = useFilters((s) => s.filters);
  const setFilters = useFilters((s) => s.setFilters);
  const sort = useFilters((s) => s.sort);
  const setSort = useFilters((s) => s.setSort);

  const countQuery = useCount(filters);
  const count = countQuery.data?.count ?? 0;

  // Selection store: ResultsHeader is the only place that lets you ENTER the
  // "по фильтру" mode (carries the filter, not ids). Once in filter-mode the
  // TouchCart shows "вся выборка по фильтру" + "случайно N из них".
  const selMode = useSelection((s) => s.mode);
  const selCount = useSelection((s) => s.count);
  const selIds = useSelection((s) => s.ids);
  const selSampleN = useSelection((s) => s.sampleN);
  const selectAllByFilter = useSelection((s) => s.selectAllByFilter);
  const selectRandomFromFilter = useSelection((s) => s.selectRandomFromFilter);
  const clearSelection = useSelection((s) => s.clear);
  const idsCount = selMode === "ids" ? (selIds?.length ?? 0) : 0;

  // Compact "выбрать случайные N" control (filter-mode, server-side sample).
  const [randomNInput, setRandomNInput] = useState("");
  // Cap the "случайные N" sample at the filtered count — you can't pick more than exist.
  const randomMax = count;
  const randomN = Number.parseInt(randomNInput, 10);
  const randomNValid = Number.isInteger(randomN) && randomN >= 1 && randomN <= randomMax;
  const selectRandom = () => {
    if (!randomNValid) return;
    selectRandomFromFilter(filters, count, randomN);
  };

  const chips = useMemo(() => flattenChips(filters, refs), [filters, refs]);

  // Set-membership operators whose array value can lose one element and stay valid.
  const SET_OPERATORS = new Set(["among", "includes", "in"]);

  /**
   * Remove one value from its clause, preserving the clause's ORIGINAL operator
   * (child_of / contains / between / …) and every sibling clause of the same field.
   * The old version rebuilt each same-field clause as eq/among, silently dropping
   * non-set operators and clobbering neighbours (F49).
   */
  const removeChip = (field: string, value: string) => {
    const next: FilterClause[] = [];
    for (const c of filters) {
      if (c.field !== field) {
        next.push(c);
        continue;
      }
      if (Array.isArray(c.value) && SET_OPERATORS.has(c.operator)) {
        const values = c.value.filter((v) => String(v) !== value);
        if (values.length === 0) continue; // clause emptied → drop it
        next.push({ ...c, value: values }); // keep the original operator
        continue;
      }
      // Scalar (eq/contains/child_of/…) or range (between) clause: it maps to a single
      // chip. Drop it only when THIS chip is the one removed; otherwise keep it intact.
      if (String(c.value) === value) continue;
      next.push(c);
    }
    setFilters(next);
  };

  const visibility = useColumns((s) => s.visibility);
  const toggleColumn = useColumns((s) => s.toggle);

  const { field: sortField, desc } = parseSort(sort);
  const sortFieldKey = sortField ?? SORT_DEFAULT_FIELD;
  const sortLabel = FIELD_BY_KEY[sortFieldKey]?.label ?? sortFieldKey;
  const sortArrow = sortField ? (desc ? "↓" : "↑") : "↑";
  const cycleSortBy = () => setSort(cycleSort(sort, SORT_DEFAULT_FIELD));

  return (
    <div
      className="flex flex-col gap-[8px] border-b bg-[var(--panel)] px-[18px] py-[11px]"
      style={{ borderColor: "var(--border-hex)" }}
    >
      {/* row 1 — controls (left) + right cluster (ml-auto), no chips */}
      <div className="flex flex-wrap items-center gap-[10px]">
      <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
        Найдено{" "}
        <b className="font-mono tabular-nums" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
          {countQuery.isPending ? "…" : count.toLocaleString("ru-RU")}
        </b>
      </span>

      {/* selection control — the only entry into "по фильтру" mode. */}
      {selMode === "filter" ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "var(--accent-weak)",
            color: "var(--accent-weak-text)",
            fontSize: "11.5px",
            padding: "3px 9px",
            borderRadius: 7,
            whiteSpace: "nowrap",
          }}
        >
          {selSampleN != null ? (
            <span>
              Выбрано: случайно{" "}
              <b className="font-mono tabular-nums" style={{ fontWeight: 600 }}>
                {selSampleN.toLocaleString("ru-RU")}
              </b>{" "}
              из{" "}
              <b className="font-mono tabular-nums" style={{ fontWeight: 600 }}>
                {selCount.toLocaleString("ru-RU")}
              </b>
            </span>
          ) : (
            <span>
              Выбрана вся выборка:{" "}
              <b className="font-mono tabular-nums" style={{ fontWeight: 600 }}>
                {selCount.toLocaleString("ru-RU")}
              </b>
            </span>
          )}
          <button
            type="button"
            onClick={() => clearSelection()}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--accent-weak-text)",
              cursor: "pointer",
              fontSize: "11.5px",
              lineHeight: 1,
              padding: 0,
              fontWeight: 500,
              textDecoration: "underline",
            }}
          >
            Очистить
          </button>
        </span>
      ) : (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          {idsCount > 0 && (
            <span style={{ fontSize: 12, color: "var(--text-2)" }}>
              Выбрано:{" "}
              <b className="font-mono tabular-nums" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                {idsCount.toLocaleString("ru-RU")}
              </b>
            </span>
          )}
          <button
            type="button"
            onClick={() => selectAllByFilter(filters, count)}
            disabled={countQuery.isPending || count === 0}
            style={{
              border: "1px solid var(--accent-hex)",
              background: "var(--accent-weak)",
              color: "var(--accent-hex)",
              cursor: countQuery.isPending || count === 0 ? "default" : "pointer",
              fontSize: "11.5px",
              fontWeight: 500,
              padding: "3px 9px",
              borderRadius: 7,
              whiteSpace: "nowrap",
              opacity: countQuery.isPending || count === 0 ? 0.5 : 1,
            }}
          >
            {idsCount > 0
              ? `Выбрать все ${count.toLocaleString("ru-RU")} по фильтру`
              : `Выбрать все ${count.toLocaleString("ru-RU")}`}
          </button>

          {/* Compact "выбрать случайные N из {count}" — enters filter-mode w/ sampleN. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "11.5px",
              color: "var(--text-2)",
              whiteSpace: "nowrap",
            }}
          >
            <span>Выбрать случайные</span>
            <input
              type="number"
              min={1}
              max={randomMax}
              inputMode="numeric"
              placeholder="N"
              value={randomNInput}
              onChange={(e) => setRandomNInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") selectRandom();
              }}
              aria-label="Число случайных активистов"
              data-testid="header-random-n-input"
              className="tabular-nums"
              style={{
                width: 92,
                height: 24,
                padding: "0 7px",
                borderRadius: 7,
                border: "1px solid var(--border-hex)",
                background: "var(--panel-subtle, #F7F9FC)",
                fontSize: "11.5px",
              }}
            />
            <span>
              из{" "}
              <b className="font-mono tabular-nums" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                {count.toLocaleString("ru-RU")}
              </b>
            </span>
            <button
              type="button"
              onClick={selectRandom}
              disabled={!randomNValid || countQuery.isPending}
              data-testid="header-random-n-button"
              style={{
                border: "1px solid var(--accent-hex)",
                background: "var(--accent-weak)",
                color: "var(--accent-hex)",
                cursor: !randomNValid || countQuery.isPending ? "default" : "pointer",
                fontSize: "11.5px",
                fontWeight: 500,
                padding: "3px 9px",
                borderRadius: 7,
                whiteSpace: "nowrap",
                opacity: !randomNValid || countQuery.isPending ? 0.5 : 1,
              }}
            >
              Выбрать
            </button>
          </span>
        </span>
      )}

      {/* right cluster */}
      <div className="ml-auto flex items-center gap-[10px]" style={{ fontSize: 12.5 }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Показать или скрыть колонки"
              style={{
                border: "none",
                background: "transparent",
                color: "var(--text-2)",
                cursor: "pointer",
                padding: 0,
                whiteSpace: "nowrap",
                font: "inherit",
              }}
            >
              Колонки ▾
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem]">
            <DropdownMenuLabel>Колонки</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visibility[col.key]}
                disabled={col.pinned}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggleColumn(col.key)}
              >
                {col.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span style={{ color: "var(--control-border)" }}>·</span>
        <button
          type="button"
          onClick={cycleSortBy}
          style={{
            border: "none",
            background: "transparent",
            color: "var(--text-2)",
            cursor: "pointer",
            padding: 0,
            whiteSpace: "nowrap",
          }}
        >
          Сортировка: {sortLabel} {sortArrow}
        </button>
        <span style={{ color: "var(--control-border)" }}>·</span>
        <ExportButton />
      </div>
      </div>

      {/* row 2 — active-facet chips (their own wrap zone; rendered only if any) */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-[8px]">
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>Фильтры:</span>
          {chips.map((chip) => (
            <span
              key={`${chip.field}:${chip.value}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--accent-weak)",
                color: "var(--accent-weak-text)",
                fontSize: "11.5px",
                padding: "3px 9px",
                borderRadius: 7,
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ opacity: 0.75 }}>{chip.fieldLabel}:</span>
              <span style={{ fontWeight: 500 }}>{chip.valueLabel}</span>
              <button
                type="button"
                aria-label={`Убрать ${chip.fieldLabel}: ${chip.valueLabel}`}
                onClick={() => removeChip(chip.field, chip.value)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--accent-weak-text)",
                  cursor: "pointer",
                  fontSize: "12px",
                  lineHeight: 1,
                  padding: 0,
                  opacity: 0.7,
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
